import React, { FC, forwardRef, useMemo } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';

import { Address } from 'ia-react-core/~/interfaces/Address';
import requiredValidator from '../../../validators/requiredValidator';
import filterPropsByComponent from '../../../utils/components/_filterPropsByComponent';
import Control from '../Control';
import { AddressSuggestControlDefaultProps, AddressSuggestInputControlComposedProps, AddressSuggestInputControlValidationProps } from './AddressSuggestControl';
import { PostCanadaFindItem } from '../AddressSuggestInput';

const withControl = (WrappedComponent: FC<AddressSuggestInputControlComposedProps>) => {
  const WithControl = forwardRef<HTMLInputElement, AddressSuggestInputControlComposedProps>(({ required, recommended, ...props }, ref) => {
    const defaultValidators = useMemo(() => ({
      required: requiredValidator,
      recommended: { validator: requiredValidator, type: 'warning' },
    }), []);

    const TypedControl = Control<Address | PostCanadaFindItem | string, Address | string, HTMLInputElement, AddressSuggestInputControlValidationProps>;

    return (
      <TypedControl
        {...filterPropsByComponent(props, TypedControl)}
        required={required}
        recommended={recommended}
        defaultValidators={defaultValidators}
      >
        {({
          id, value, isValid, hasWarning, hasCaution, hasInfo, isTouched, errorMessagesId, onValueChange: onValueChangeCallback, onTouched: onTouchedCallback,
        }) => (
          <WrappedComponent
            id={id}
            data-control
            {...props}
            value={value as Address | PostCanadaFindItem}
            isValid={isValid}
            hasWarning={hasWarning}
            hasCaution={hasCaution}
            hasInfo={hasInfo}
            isTouched={isTouched}
            aria-errormessage={errorMessagesId}
            ref={ref}
            required={required}
            onValueChangeCallback={onValueChangeCallback}
            onTouchedCallback={onTouchedCallback}
          />
        )}
      </TypedControl>
    );
  }) as FC<AddressSuggestInputControlComposedProps>;

  WithControl.defaultProps = AddressSuggestControlDefaultProps;

  return hoistNonReactStatics(WithControl, WrappedComponent);
};

export default withControl;
